/**
 * Hook para un menú/dropdown anclado a un botón y pensado para renderizarse en
 * un PORTAL a `document.body` con posición fija.
 *
 * Motivación: el shell del portal vive en un contenedor de ancho fijo
 * (`max-w-portal`) con `overflow-hidden`. Un dropdown posicionado con
 * `absolute` dentro de ese contenedor se recorta cuando la página es corta (la
 * card mide lo que el contenido). Renderizándolo en un portal con
 * `position: fixed` anclado al botón, el menú escapa de cualquier ancestro con
 * overflow y nunca se corta.
 *
 * El hook entrega el estado y los refs; el componente decide qué renderiza
 * dentro del portal (ancho, contenido). Cierra con click-fuera, ESC, scroll y
 * resize. `cerrarEn` fuerza el cierre ante un cambio externo (ej. el pathname).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface MenuAnclado {
  abierto: boolean;
  /** Coordenadas fijas (viewport) para el portal: top + offset desde la
   *  derecha. `null` hasta el primer cálculo. */
  coords: { top: number; right: number } | null;
  botonRef: React.RefObject<HTMLButtonElement>;
  menuRef: React.RefObject<HTMLDivElement>;
  alternar: () => void;
  cerrar: () => void;
}

export function useMenuAnclado(cerrarEn?: unknown): MenuAnclado {
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(
    null,
  );
  const botonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const recalcular = useCallback(() => {
    const b = botonRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, right: window.innerWidth - r.right });
  }, []);

  const cerrar = useCallback(() => setAbierto(false), []);

  const alternar = useCallback(() => {
    if (!abierto) recalcular();
    setAbierto((v) => !v);
  }, [abierto, recalcular]);

  useEffect(() => {
    if (!abierto) return;
    function onClickFuera(e: MouseEvent) {
      const t = e.target as Node;
      if (!botonRef.current?.contains(t) && !menuRef.current?.contains(t)) {
        setAbierto(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAbierto(false);
    }
    // Cerrar ante scroll de la PÁGINA (el dropdown anclado quedaría desanclado
    // del botón). Pero IGNORAR scroll interno del propio menú —ej. la lista de
    // notificaciones del Bell o una hoja inferior mobile con scroll propio—,
    // que de otro modo cerraría el panel al desplazar su contenido.
    function onScroll(e: Event) {
      if (menuRef.current?.contains(e.target as Node)) return;
      setAbierto(false);
    }
    document.addEventListener('mousedown', onClickFuera);
    document.addEventListener('keydown', onKey);
    // Capture para detectar scroll de cualquier contenedor, no solo window.
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('mousedown', onClickFuera);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto, cerrar]);

  // Cierra ante un cambio externo (ej. navegación / pathname).
  useEffect(() => {
    setAbierto(false);
  }, [cerrarEn]);

  return { abierto, coords, botonRef, menuRef, alternar, cerrar };
}
